import { useEffect, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";

import { Button, Modal, SearchBar, Tooltip, setSnack, useManualModalState } from "@synthesis/shared-ui";
import { useListRootTab } from "@synthesis/shared-services/filesystem-gge/directories/hooks";
import { findByProjectIdQueryKey, useUpdatePlacements } from "@synthesis/shared-services/models/modelsDrawingsController/hooks";

import type { DirectoryListResponse, FileMetaDto } from "../../../api";
import { useStores } from "../../../store";
import { fileMatchesTab } from "../../../utils/file-tab-filter";
import type { ContextMenuStateFunctions } from "../../files/hooks";
import { TableView } from "../../files/local-components";
import type { BindDrawingTarget } from "../drawings-collection.lib";

type ModalBindToModelProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  drawings: BindDrawingTarget[];
};

const MODELS_PICKER_TABLE_SETTINGS_KEY = "table-settings-models-picker-table";

const noopFileHandler = (_file?: FileMetaDto) => undefined;
const noopHandler = () => undefined;

const pickerStateFunctions: ContextMenuStateFunctions = {
  openViewer: noopFileHandler,
  openDoc: noopFileHandler,
  handleClickReName: noopHandler,
  handleDeleteFile: noopHandler,
  setIsMarkupOpen: noopHandler,
  handleClickAddNewVersion: noopHandler,
  setIsOpenMeta: noopHandler,
  handleClickConvert: noopHandler,
};

const toFileMetaList = (responseData: DirectoryListResponse[]): FileMetaDto[] =>
  responseData.filter((el) => el.fileMeta && !el.fileMeta.isDeleted).map((el) => el.fileMeta as FileMetaDto);

const getSharedModelIds = (drawings: BindDrawingTarget[]): string[] => {
  const first = drawings[0];

  if (!first) {
    return [];
  }

  return drawings.slice(1).reduce((acc, drawing) => acc.filter((id) => drawing.modelIds.includes(id)), first.modelIds);
};

const uniqueIds = (ids: string[]) => [...new Set(ids.filter(Boolean))];

const areSameIds = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  const rightSet = new Set(right);

  return left.every((id) => rightSet.has(id));
};

const getPlacementModelIds = (drawing: BindDrawingTarget, selectedModelIds: string[], sharedModelIds: string[]) => {
  const sharedIds = new Set(sharedModelIds);
  const privateIds = drawing.modelIds.filter((id) => !sharedIds.has(id));

  return uniqueIds([...privateIds, ...selectedModelIds]);
};

const isRemovingProtectedBinding = (drawings: BindDrawingTarget[], selectedModelIds: string[], sharedModelIds: string[]) => {
  return drawings.some((drawing) => {
    const nextIds = new Set(getPlacementModelIds(drawing, selectedModelIds, sharedModelIds));

    return drawing.protectedModelIds.some((modelId) => !nextIds.has(modelId));
  });
};

const willClearAllBindings = (drawings: BindDrawingTarget[], selectedModelIds: string[], sharedModelIds: string[]) =>
  drawings.every((drawing) => getPlacementModelIds(drawing, selectedModelIds, sharedModelIds).length === 0);

const ModalBindToModelContent = observer(({ drawings, isOpen }: { drawings: BindDrawingTarget[]; isOpen: boolean }) => {
  const { closeModal } = useManualModalState();
  const { generalStore } = useStores();
  const queryClient = useQueryClient();
  const updatePlacements = useUpdatePlacements();
  const [search, setSearch] = useState("");
  const [selectedModels, setSelectedModels] = useState<FileMetaDto[]>([]);
  const [hasSeededSelection, setHasSeededSelection] = useState(false);
  const [isUnbindConfirmOpen, setIsUnbindConfirmOpen] = useState(false);

  const projectId = generalStore.projectId ?? undefined;
  const hasDrawings = drawings.length > 0;

  const selectionSeedKey = useMemo(
    () => drawings.map((drawing) => `${drawing.id}:${[...drawing.modelIds].toSorted().join(",")}`).join("|"),
    [drawings],
  );
  const sharedModelIds = useMemo(() => getSharedModelIds(drawings), [drawings]);
  const pickerSelectedIds = useMemo(() => selectedModels.map((model) => model.id), [selectedModels]);
  const removesProtectedBinding = useMemo(
    () => isRemovingProtectedBinding(drawings, pickerSelectedIds, sharedModelIds),
    [drawings, pickerSelectedIds, sharedModelIds],
  );
  const hasExistingBindings = drawings.some((drawing) => drawing.modelIds.length > 0);
  const canConfirm = hasDrawings && !updatePlacements.isPending && (pickerSelectedIds.length > 0 || hasExistingBindings);

  const { data: models = [], isLoading } = useListRootTab(
    { path: { id: projectId ?? "", directoryTab: "IFC" } },
    {
      query: {
        enabled: isOpen && !!projectId,
        select: (responseData) => toFileMetaList(responseData).filter((file) => fileMatchesTab(file, "models")),
      },
    },
  );

  useEffect(() => {
    if (!isOpen) {
      setHasSeededSelection(false);
      setSelectedModels([]);
      setSearch("");
      setIsUnbindConfirmOpen(false);
      return;
    }

    if (isLoading || hasSeededSelection) {
      return;
    }

    localStorage.removeItem(MODELS_PICKER_TABLE_SETTINGS_KEY);
    setSearch("");
    const sharedIds = new Set(sharedModelIds);
    setSelectedModels(models.filter((model) => sharedIds.has(model.id)));
    setHasSeededSelection(true);
  }, [isOpen, isLoading, hasSeededSelection, models, sharedModelIds]);

  const handlePickerSelectionChange = (files: FileMetaDto[]) => {
    setSelectedModels((prev) => {
      if (prev.length === files.length) {
        const prevIds = new Set(prev.map((file) => file.id));
        if (files.every((file) => prevIds.has(file.id))) {
          return prev;
        }
      }

      return files;
    });
    setIsUnbindConfirmOpen(false);
  };

  const handleConfirm = async () => {
    const selectedModelIds = uniqueIds(selectedModels.map((model) => model.id));
    const isUnbind = hasExistingBindings && willClearAllBindings(drawings, selectedModelIds, sharedModelIds);

    if (!hasDrawings || updatePlacements.isPending) {
      return;
    }

    const updates = drawings.flatMap((drawing) => {
      const data = getPlacementModelIds(drawing, selectedModelIds, sharedModelIds);

      if (areSameIds(drawing.modelIds, data)) {
        return [];
      }

      return [{ drawingId: drawing.id, data }];
    });

    try {
      if (updates.length > 0) {
        await Promise.all(
          updates.map(({ drawingId, data }) =>
            updatePlacements.mutateAsync({
              path: { drawingId },
              body: data,
            }),
          ),
        );
      }

      if (projectId) {
        await queryClient.invalidateQueries({ queryKey: findByProjectIdQueryKey({ path: { projectId } }) });
      }

      setSnack({
        title: isUnbind
          ? drawings.length === 1
            ? drawings[0]?.name
              ? `Привязка чертежа «${drawings[0].name}» удалена`
              : "Привязка чертежа удалена"
            : `Привязка удалена для чертежей: ${drawings.length}`
          : drawings.length === 1
            ? drawings[0]?.name
              ? `Чертёж «${drawings[0].name}» привязан к модели`
              : "Чертёж привязан к модели"
            : `Привязано чертежей: ${drawings.length}`,
        messageType: "info",
      });
      setIsUnbindConfirmOpen(false);
      closeModal();
    } catch {
      setSnack({
        title: isUnbind
          ? drawings.length === 1
            ? "Не удалось удалить привязку чертежа"
            : "Не удалось удалить привязку выбранных чертежей"
          : drawings.length === 1
            ? "Не удалось привязать чертёж к модели"
            : "Не удалось привязать выбранные чертежи к модели",
        messageType: "error",
      });
    }
  };

  const drawingNamesLabel = drawings
    .map((drawing) => drawing.name)
    .filter(Boolean)
    .map((name) => `«${name}»`)
    .join(", ");
  const title = drawingNamesLabel ? `Выбор .ifc для привязки ${drawingNamesLabel}` : "Выбор .ifc для привязки";

  const confirmButton = (
    <Button
      size="lg"
      theme="primary"
      disabled={!canConfirm}
      onClick={() => (removesProtectedBinding ? setIsUnbindConfirmOpen(true) : void handleConfirm())}
    >
      Подтвердить
    </Button>
  );

  return (
    <Modal.Content className="flex h-[min(670px,90vh)] w-[min(1106px,95vw)] max-w-[95vw] flex-col overflow-hidden !p-0">
      <Modal.Title className="min-w-0 overflow-hidden">
        <Tooltip description={title} arrowSide="bottom">
          <span className="block min-w-0 truncate">{title}</span>
        </Tooltip>
      </Modal.Title>

      <div className="bg-neutrals-white flex min-h-0 flex-1 flex-col gap-6 px-6 pt-6">
        <SearchBar size="sm" className="w-[320px]" placeholder="Поиск трёхмерных моделей" clearable value={search} onChange={setSearch} />

        <div className="min-h-0 flex-1 pb-0">
          {hasSeededSelection ? (
            <TableView
              key={selectionSeedKey || "empty-seed"}
              variant="models-picker"
              pickerFiles={models}
              pickerSearch={search}
              pickerIsLoading={isLoading}
              pickerSelectedIds={pickerSelectedIds}
              onPickerSelectionChange={handlePickerSelectionChange}
              onClickModel={noopFileHandler}
              stateFunctions={pickerStateFunctions}
              setSelectedFile={noopFileHandler}
              disabledRight
            />
          ) : null}
        </div>
      </div>

      <div className="bg-neutrals-99 flex shrink-0 gap-4 px-8 pb-6 pt-3">
        {confirmButton}
        <Button size="lg" theme="secondary" onClick={closeModal}>
          Отмена
        </Button>
      </div>

      {isUnbindConfirmOpen ? (
        <div className="absolute inset-0 z-[10000] flex items-center justify-center shadow-xl" onClick={() => setIsUnbindConfirmOpen(false)}>
          <div
            className="bg-neutrals-white flex w-[min(360px,90%)] flex-col gap-4 rounded-[14px] p-6 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="text-type-primary">Чертеж размещен в модели. Удалить привязку?</p>
            <div className="flex items-center justify-between gap-2">
              <Button
                size="sm"
                theme="secondary"
                disabled={updatePlacements.isPending}
                onClick={() => {
                  setIsUnbindConfirmOpen(false);
                  void handleConfirm();
                }}
              >
                Удалить
              </Button>
              <Button size="sm" theme="primary" onClick={() => setIsUnbindConfirmOpen(false)}>
                Отменить
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Modal.Content>
  );
});

export const ModalBindToModel = ({ drawings, ...props }: ModalBindToModelProps) => (
  <Modal.Root manual {...props}>
    <ModalBindToModelContent drawings={drawings} isOpen={props.isOpen} />
  </Modal.Root>
);
